import dynamic from 'next/dynamic';
import Link from 'next/link';
import React, { Suspense } from 'react';
import Meta from '../components/Meta';
import { getTramPlanConfig } from '../data/tramPlans/config';

const MapSkeleton = () => (
  <div className="flex h-full w-full items-center justify-center bg-[var(--c-bg)] neo-civic-grid">
    <div className="relative flex flex-col items-center gap-4">
      <div className="relative flex h-16 w-16 items-center justify-center rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] shadow-lg">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-transparent border-t-[var(--c-accent)]" />
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-[var(--c-text-primary)] font-['Space_Grotesk']">
          Loading Tram Plan
        </p>
        <p className="mt-1 text-xs uppercase tracking-wide text-[var(--c-text-secondary)] font-['DM_Mono']">
          Initializing Transit View
        </p>
      </div>
    </div>
  </div>
);

const TramPlanMap = dynamic(() => import('../components/tram/TramPlanMap'), {
  ssr: false,
  loading: () => <MapSkeleton />,
});

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(err, info) {
    console.error('TramPlanMap encountered an error', err, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-6 bg-[var(--c-bg)] p-8 text-center">
          <div className="max-w-md space-y-4">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)]">
              <svg
                className="h-8 w-8 text-[var(--c-error)]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-[var(--c-text-primary)] font-['Space_Grotesk']">
                Map Loading Error
              </h2>
              <p className="text-sm leading-relaxed text-[var(--c-text-secondary)]">
                The tram plan map encountered an issue during initialization. Please refresh the page and try again.
              </p>
            </div>
            <button
              onClick={() => location.assign(location.href)}
              className="rounded-md bg-[var(--c-accent)] px-4 py-2 text-sm font-semibold text-[var(--c-bg)] transition-colors hover:bg-[var(--c-accent)]/90"
            >
              Reload Map
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function EdinburghTramPlanPage() {
  const config = getTramPlanConfig('edi');

  React.useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const previousRootOverflow = root.style.overflow;
    const previousRootOverscroll = root.style.overscrollBehavior;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyOverscroll = body.style.overscrollBehavior;
    const previousBodyPaddingRight = body.style.paddingRight;
    const previousBodyPosition = body.style.position;
    const previousBodyTop = body.style.top;
    const previousBodyLeft = body.style.left;
    const previousBodyRight = body.style.right;
    const previousBodyWidth = body.style.width;
    const lockedScrollY = window.scrollY;
    const scrollbarWidth = window.innerWidth - root.clientWidth;

    root.style.overflow = 'hidden';
    root.style.overscrollBehavior = 'none';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    body.style.position = 'fixed';
    body.style.top = `-${lockedScrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';

    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      root.style.overflow = previousRootOverflow;
      root.style.overscrollBehavior = previousRootOverscroll;
      body.style.overflow = previousBodyOverflow;
      body.style.overscrollBehavior = previousBodyOverscroll;
      body.style.paddingRight = previousBodyPaddingRight;
      body.style.position = previousBodyPosition;
      body.style.top = previousBodyTop;
      body.style.left = previousBodyLeft;
      body.style.right = previousBodyRight;
      body.style.width = previousBodyWidth;
      window.scrollTo(0, lockedScrollY);
    };
  }, []);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': 'https://www.city-scope.co.uk/edi-tram-plan#webpage',
    url: 'https://www.city-scope.co.uk/edi-tram-plan',
    name: 'Edinburgh Tram Map and Tram Plan',
    description:
      'Interactive Edinburgh tram map and tram plan for exploring tram routes, rail connections, stations, and transit planning context.',
    isPartOf: { '@id': 'https://www.city-scope.co.uk/#website' },
    about: [
      'Edinburgh tram map',
      'Edinburgh tram plan',
      'Edinburgh rail network',
      'Public transport planning',
    ],
  };

  return (
    <>
      <Meta
        title="Edinburgh Tram Map and Tram Plan | City Scope"
        description="Explore an interactive Edinburgh tram map and tram plan with rail connections, stations, and transit planning context."
        canonical="/edi-tram-plan"
        ogImage="https://www.city-scope.co.uk/edinburgh-tram-x-card.png"
        ogImageAlt="City Scope Edinburgh tram map, showing an illustrated tram and rail network with existing links and indicative extensions."
        ogImageType="image/png"
        ogImageWidth={1734}
        ogImageHeight={907}
        structuredData={structuredData}
      />

      <div
        className="planning-register relative h-[100dvh] min-h-[36rem] w-full overflow-hidden bg-[var(--c-bg)]"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        {/* Static HTML panel for crawlers/agents (map itself is client-rendered). */}
        <aside className="sr-only">
          <div>
            <div>Edinburgh Tram Plan</div>
            <p>
              This page is an interactive map view. For a text-first crawlable index, see:
            </p>
            <div>
              <Link
                href="/planning-updates"
              >
                Planning Updates
              </Link>
            </div>
          </div>
        </aside>

        <Suspense fallback={<MapSkeleton />}>
          <ErrorBoundary>
            <TramPlanMap cityKey="edi" />
          </ErrorBoundary>
        </Suspense>
      </div>

      <section aria-label="About the Edinburgh tram map" className="planning-register neo-civic-grid border-t border-[var(--c-border)] bg-[var(--c-bg)] text-[var(--c-text-primary)]">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
          <p className="pr-section-label">About the map</p>
          <div className="mt-5 grid gap-10 lg:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.65fr)]">
            <div>
              <h1 className="pr-heading max-w-3xl">Edinburgh&apos;s tram and rail network, read as one connected city.</h1>
              <p className="mt-5 max-w-3xl text-base leading-7 text-[var(--c-text-secondary)]">
                {config?.description || 'Interactive Edinburgh tram and rail plan'}. Compare operational links with indicative extensions, locate stops and interchanges, and inspect the development context around the network.
              </p>
              <p className="mt-4 max-w-3xl text-sm leading-6 text-[var(--c-text-secondary)]">
                Proposed routes are a CityScope concept view compiled from public material. They are not an official commitment and may change through appraisal, consultation, funding and approval.
              </p>
            </div>
            <div className="border border-[var(--c-border)] border-t-2 border-t-[var(--c-text-primary)] bg-[var(--c-surface)] p-5">
              <p className="pr-kicker pr-kicker--accent">Continue exploring</p>
              <div className="mt-4 divide-y divide-[var(--c-border)] border-y border-[var(--c-border)]">
                <Link href="/planning-map" className="flex items-center justify-between py-3 text-sm font-semibold text-[var(--c-text-primary)] hover:text-[var(--c-accent)]">
                  Planning Map <span aria-hidden="true">&rarr;</span>
                </Link>
                <Link href="/planning-updates/edi" className="flex items-center justify-between py-3 text-sm font-semibold text-[var(--c-text-primary)] hover:text-[var(--c-accent)]">
                  Edinburgh updates <span aria-hidden="true">&rarr;</span>
                </Link>
                <a href="https://www.edinburgh.gov.uk" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between py-3 text-sm font-semibold text-[var(--c-text-primary)] hover:text-[var(--c-accent)]">
                  Council tram information <span aria-hidden="true">&nearr;</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
